/**
 * Test Student Login Screen (?mod=test-student)
 * Username + password login for admin-created (legacy/test) students — the
 * ones managed through the admin panel rather than a family account. Styled
 * with the portal-root visual identity (student design system / family-auth
 * dark theme), unlike the light staff logins.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authService } from '../../services/authentication/auth-service';
import { useAuthStore } from '../../services/authentication/auth-store';
import { TurnstileWidget } from '../../components/turnstile-widget';
import { isCaptchaEnabled } from '../../components/captcha';
import type { TurnstileHandle } from '../../components/turnstile-widget';
import { LanguageSelect } from '../../components/language-select';
import { VersionFooter } from './version-footer';
import { FormField, FormMessage, Input } from '../../components/form-field';
import { Button } from '../../components/button';
import auth from './auth.module.css';

export function TestStudentLoginScreen() {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const isLoading = useAuthStore((state) => state.isLoading);
  const error = useAuthStore((state) => state.error);

  // Captcha (only enforced when a Turnstile sitekey is configured)
  const captchaEnabled = isCaptchaEnabled();
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<TurnstileHandle>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await authService.login({
      username,
      password,
      mode: 'student',
      captchaToken: captchaToken ?? undefined,
    });
    // Turnstile tokens are single-use — refresh for a possible retry
    captchaRef.current?.reset();
  };

  return (
    // student-portal + family-auth: the student design system, restyled by the
    // `:global(.family-auth)` variants beside each rule (auth.module.css and its kin)
    <div className={`${auth.loginContainer} portal-shell student-portal family-auth`}>

      <div className={auth.loginContent}>
        <div className={`${auth.loginCard} framed`}>
          <div className={auth.loginHeader}>
            <h1 className={auth.loginTitle}>{t('testStudentLogin.title')}</h1>
            <p className={auth.loginSubtitle}>{t('testStudentLogin.subtitle')}</p>
          </div>

          <div className={auth.loginLanguageSelector}>
            <LanguageSelect compact />
          </div>

          <form onSubmit={(e) => void handleSubmit(e)} className={auth.loginForm}>
            <FormField label={t('login.username')} required>
              {(field) => (
                <Input {...field}
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={t('login.enterUsername')}
                  required
                  disabled={isLoading}
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                />
              )}
            </FormField>

            <FormField label={t('login.password')} required>
              {(field) => (
                <Input {...field}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('login.enterPassword')}
                  required
                  disabled={isLoading}
                  autoComplete="current-password"
                />
              )}
            </FormField>

            <TurnstileWidget ref={captchaRef} onToken={setCaptchaToken} />

            {error && (
              <FormMessage>
                {error === 'network_error' ? t('login.networkError') : t('login.loginError')}
              </FormMessage>
            )}

            <Button size="lg" block
              type="submit"
              disabled={!username || !password || isLoading || (captchaEnabled && !captchaToken)}
            >
              {isLoading ? t('common.loading') : t('login.loginButton')}
            </Button>
          </form>

          <p className={auth.loginSignupHint}>
            <a href={window.location.pathname} className={auth.loginSignupLink}>
              {t('signup.backToHome')}
            </a>
          </p>
        </div>

        <div className={auth.loginVersionBox}>
          <VersionFooter />
        </div>
      </div>
    </div>
  );
}
